import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import BasicSetup from "./Components/BasicSetup";
import Availability from "./Components/Availability";
import Scheduling from "./Components/Scheduling";
import { getHostMeeting, getMeetingById } from "../Service/Service";
import { store } from "../../../utils/store";
import history from "../../../utils/history";
import urls from "../../../global/constants/UrlConstants";

const stepData = ["Basic Setup", "Availability", "Scheduling"];

interface CustomProps {
  isDialogOpen: boolean;
  handleClose: Function;
  userId: string;
  setUserId: Function;
  fromSettingOrLogout?: string;
}

interface MeetingLocationState {
  meetingId?: string;
  mode?: "edit" | "create";
}

const CreateMeeting = (props: CustomProps) => {
  const [activeStep, setActiveStep] = useState(0);
  const location = useLocation<MeetingLocationState>();
  const meetingId = location.state?.meetingId;
  const mode = location.state?.mode;
  const loginUserId = store.getState().auth?.userId || "";
  const [state, setState] = useState<any>({
    meetingType: "both",
    duration: 15,
    buffer: 5,
    breakEnabled: true,
    breakStart: "13:30",
    breakEnd: "14:30",
    host: loginUserId,
  });

  const setStateData = (newData: any) =>
    setState((prev: any) => ({ ...prev, ...newData }));

  useEffect(() => {
    const getMeetingData = async () => {
      const authUserId = store.getState().auth?.userId || "";

      // Create flow: host is always the logged-in user. If they already have
      // a meeting, open edit instead of failing on "one meeting per host".
      if (mode !== "edit") {
        if (authUserId && !state.host) {
          setStateData({ host: authUserId });
        }
        if (authUserId) {
          try {
            const existing: any = await getHostMeeting(authUserId);
            const existingId =
              existing?.data?._id ||
              existing?.data?.meetingId ||
              existing?.data?.id;
            if (existingId) {
              history.replace({
                pathname: urls.createMeetingViewPath,
                state: { meetingId: String(existingId), mode: "edit" },
              });
              return;
            }
          } catch (_) {
            /* no existing meeting */
          }
        }
        return;
      }

      if (mode === "edit" && meetingId) {
        const response: any = await getMeetingById(meetingId);
        const data = response?.data || {};
        const hostId =
          data.host?._id || data.host?.id || data.host || authUserId;
        setStateData({
          ...data,
          host: hostId ? String(hostId) : authUserId,
          meetingType: data.meetingType || "both",
          duration: data.duration || 15,
          buffer: data.buffer ?? 5,
          breakEnabled: data.breakEnabled !== false,
          breakStart: data.breakStart || "13:30",
          breakEnd: data.breakEnd || "14:30",
        });
      }
    };
    getMeetingData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, meetingId]);

  const nextStep = () => {
    setActiveStep((prev) => Math.min(prev + 1, stepData.length - 1));
  };

  const backStep = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const getAllContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <BasicSetup
            userId={props.userId}
            nextStep={nextStep}
            setUserId={props.setUserId}
            fromSettingOrLogout={props.fromSettingOrLogout}
            state={state}
            setStateData={setStateData}
          />
        );
      case 1:
        return (
          <Availability
            userId={props.userId}
            nextStep={nextStep}
            backStep={backStep}
            fromSettingOrLogout={props.fromSettingOrLogout}
            state={state}
            setStateData={setStateData}
          />
        );
      case 2:
        return (
          <Scheduling
            nextStep={nextStep}
            backStep={backStep}
            fromSettingOrLogout={props.fromSettingOrLogout}
            userId={props.userId}
            state={state}
            setStateData={setStateData}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex h-[calc(100vh-68px)] max-h-[calc(100vh-68px)] overflow-hidden bg-white">
      {/* Vertical stepper */}
      <aside className="hidden w-[200px] shrink-0 flex-col overflow-hidden border-r border-[#EEF0F5] bg-white lg:flex xl:w-[220px]">
        <div className="min-h-0 flex-1 overflow-auto p-5">
          <p className="mb-4 text-xs font-semibold uppercase tracking-wide text-[#9CA3AF]">
            Create Meeting
          </p>
          <ol className="relative space-y-0">
            {stepData.map((label, index) => {
              const done = index < activeStep;
              const current = index === activeStep;
              return (
                <li key={label} className="relative flex gap-3 pb-8 last:pb-0">
                  {index < stepData.length - 1 ? (
                    <span
                      className={`absolute left-[15px] top-8 h-[calc(100%-20px)] w-px ${
                        done ? "bg-[#A02448]" : "bg-[#E5E7EB]"
                      }`}
                      aria-hidden
                    />
                  ) : null}
                  <span
                    className={`relative z-[1] flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                      current
                        ? "bg-[#A02448] text-white ring-4 ring-[#F9F0F4]"
                        : done
                        ? "bg-[#A02448] text-white"
                        : "bg-white text-[#9CA3AF] ring-2 ring-[#E5E7EB]"
                    }`}
                  >
                    {done ? (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                    ) : (
                      index + 1
                    )}
                  </span>
                  <div className="min-w-0 pt-1">
                    <p
                      className={`text-sm font-semibold ${
                        current || done ? "text-[#111827]" : "text-[#9CA3AF]"
                      }`}
                    >
                      {label}
                    </p>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </aside>

      {/* Mobile step indicator */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <div className="flex shrink-0 items-center gap-2 border-b border-[#EEF0F5] bg-white px-4 py-3 lg:hidden">
          {stepData.map((label, index) => (
            <span
              key={label}
              className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                index === activeStep
                  ? "bg-[#F9F0F4] text-[#A02448]"
                  : index < activeStep
                  ? "bg-[#ECFDF5] text-emerald-600"
                  : "bg-[#F3F4F6] text-[#9CA3AF]"
              }`}
            >
              {index + 1}. {label}
            </span>
          ))}
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-4 sm:p-5 lg:p-6">
          {getAllContent(activeStep)}
        </div>
      </div>
    </div>
  );
};

export default CreateMeeting;
